<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  MinusSignIcon,
  PlusSignIcon,
  KeyframeAlignCenterIcon,
  Undo03Icon,
  Redo03Icon,
} from '@hugeicons/core-free-icons';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';

defineProps({
  canUndo: { type: Boolean, default: false },
  canRedo: { type: Boolean, default: false },
});

const emit = defineEmits(['undo', 'redo']);

const { t } = useI18n();

const scrollRef = ref(null);
const zoomIdx = ref(2);
const isPanning = ref(false);

const panState = {
  pointerId: null,
  startX: 0,
  startY: 0,
  scrollLeft: 0,
  scrollTop: 0,
};

const ZOOM_CLASSES = [
  'scale-[0.72]',
  'scale-[0.86]',
  'scale-100',
  'scale-[1.10]',
  'scale-[1.25]',
];

const zoomClass = computed(
  () => ZOOM_CLASSES[zoomIdx.value] ?? ZOOM_CLASSES[2]
);

const zoomIn = () => {
  zoomIdx.value = Math.min(zoomIdx.value + 1, ZOOM_CLASSES.length - 1);
};

const zoomOut = () => {
  zoomIdx.value = Math.max(zoomIdx.value - 1, 0);
};

const centerView = () => {
  zoomIdx.value = 2;
  nextTick(() => {
    const el = scrollRef.value;
    if (!el) return;
    el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    el.scrollTop = Math.max(0, (el.scrollHeight - el.clientHeight) / 2);
  });
};

const shouldIgnorePan = target =>
  Boolean(
    target?.closest?.(
      'button, a, input, textarea, select, [role="menu"], [data-no-canvas-pan]'
    )
  );

const endPan = () => {
  if (!isPanning.value) return;
  const el = scrollRef.value;
  if (el && panState.pointerId != null) {
    try {
      el.releasePointerCapture(panState.pointerId);
    } catch {
      // pointer may already be released
    }
  }
  isPanning.value = false;
  panState.pointerId = null;
};

const onPanStart = event => {
  if (event.button !== 0 || shouldIgnorePan(event.target)) return;

  const el = scrollRef.value;
  if (!el) return;

  isPanning.value = true;
  panState.pointerId = event.pointerId;
  panState.startX = event.clientX;
  panState.startY = event.clientY;
  panState.scrollLeft = el.scrollLeft;
  panState.scrollTop = el.scrollTop;

  el.setPointerCapture(event.pointerId);
  event.preventDefault();
};

const onPanMove = event => {
  if (!isPanning.value || event.pointerId !== panState.pointerId) return;

  const el = scrollRef.value;
  if (!el) return;

  el.scrollLeft = panState.scrollLeft - (event.clientX - panState.startX);
  el.scrollTop = panState.scrollTop - (event.clientY - panState.startY);
};

onMounted(() => {
  nextTick(centerView);
});

defineExpose({ centerView });

const toolBtnClass =
  'flex size-7 shrink-0 items-center justify-center rounded-md text-n-slate-11 transition-colors hover:bg-n-alpha-2 disabled:pointer-events-none disabled:opacity-40 dark:hover:bg-n-alpha-3';

const toolbarWrapClass =
  'pointer-events-none absolute top-4 left-6 z-10 flex flex-col gap-2 rtl:left-auto rtl:right-6';

const cardClass =
  'pointer-events-auto flex flex-col gap-0.5 rounded-lg border border-n-weak/90 bg-n-solid-1/90 p-1 shadow-sm backdrop-blur-sm dark:border-n-strong dark:bg-n-solid-2/90';

const scrollSurfaceClass =
  'size-full min-h-0 cursor-grab overflow-auto overscroll-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

const iconSize = 20;
</script>

<template>
  <div
    class="relative min-h-0 flex-1 overflow-hidden bg-[radial-gradient(rgb(235_240_245)_1.2px,transparent_0)] bg-[length:16px_16px] dark:bg-[radial-gradient(rgb(41_63_81)_1.2px,transparent_0)]"
  >
    <div
      ref="scrollRef"
      :class="[scrollSurfaceClass, { 'cursor-grabbing': isPanning }]"
      @pointerdown="onPanStart"
      @pointermove="onPanMove"
      @pointerup="endPan"
      @pointercancel="endPan"
      @lostpointercapture="endPan"
    >
      <div
        class="flex min-h-full min-w-full w-max items-stretch justify-center"
      >
        <div class="w-64 shrink-0 sm:w-80" aria-hidden="true" />
        <div
          class="flex min-h-full min-w-max items-center justify-center pt-64 pb-96 sm:pt-80 sm:pb-[28rem]"
        >
          <div
            class="origin-center transition-transform duration-200 ease-out"
            :class="zoomClass"
          >
            <slot />
          </div>
        </div>
        <div class="w-64 shrink-0 sm:w-80" aria-hidden="true" />
      </div>
    </div>

    <div class="pointer-events-none absolute inset-0 z-10">
      <div
        :class="toolbarWrapClass"
        role="toolbar"
        :aria-label="t('AUTOMATION.FLOW.CANVAS_TOOLBAR')"
      >
        <div :class="cardClass">
          <button
            type="button"
            :class="toolBtnClass"
            :title="t('AUTOMATION.FLOW.ZOOM_IN')"
            :aria-label="t('AUTOMATION.FLOW.ZOOM_IN')"
            :disabled="zoomIdx >= ZOOM_CLASSES.length - 1"
            @click="zoomIn"
          >
            <HugeIcon
              :icon="PlusSignIcon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
            />
          </button>
          <button
            type="button"
            :class="toolBtnClass"
            :title="t('AUTOMATION.FLOW.ZOOM_OUT')"
            :aria-label="t('AUTOMATION.FLOW.ZOOM_OUT')"
            :disabled="zoomIdx <= 0"
            @click="zoomOut"
          >
            <HugeIcon
              :icon="MinusSignIcon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
            />
          </button>
          <button
            type="button"
            :class="toolBtnClass"
            :title="t('AUTOMATION.FLOW.CENTER_VIEW')"
            :aria-label="t('AUTOMATION.FLOW.CENTER_VIEW')"
            @click="centerView"
          >
            <HugeIcon
              :icon="KeyframeAlignCenterIcon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
            />
          </button>
        </div>
        <div :class="cardClass">
          <button
            type="button"
            :class="toolBtnClass"
            :title="t('AUTOMATION.FLOW.UNDO')"
            :aria-label="t('AUTOMATION.FLOW.UNDO')"
            :disabled="!canUndo"
            @click="emit('undo')"
          >
            <HugeIcon
              :icon="Undo03Icon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
            />
          </button>
          <button
            type="button"
            :class="toolBtnClass"
            :title="t('AUTOMATION.FLOW.REDO')"
            :aria-label="t('AUTOMATION.FLOW.REDO')"
            :disabled="!canRedo"
            @click="emit('redo')"
          >
            <HugeIcon
              :icon="Redo03Icon"
              :size="iconSize"
              class="pointer-events-none shrink-0"
            />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
